





























import { Vue, Component, Prop } from 'vue-property-decorator'
import { CampoDeEtiqueta, CampoDeEtiquetaDisponivel, CampoDoModeloDeEtiqueta, TipoDeAlinhamentoDeTexto, montarTextoDePrecoDaTabela } from '@/models/ModeloDeEtiqueta'
import type { TamanhoDaEtiqueta } from '@/models/EtiquetaDePreco'
import {
	PONTOS_POR_CM,
	QUEBRA_DE_LINHA,
	TextoAjustado,
	ajustarTextoNaCaixa,
} from '@/shareds/etiquetas/ajuste-de-texto-de-etiqueta'
import { larguraDoSimboloEmCm, moduloDoCodigoDeBarrasEmDots, moduloEmCm } from '@/shareds/etiquetas/codigo-de-barras-de-etiqueta'

const LARGURA_MAXIMA_PX = 900
const ALTURA_MAXIMA_PX = 700

const EXEMPLOS_DE_TABELA = [
	'CARTAO CREDITO PARCELADO',
	'PIX A VISTA',
	'BOLETO 30 DIAS',
	'CARTAO DEBITO',
	'CREDIARIO',
	'DINHEIRO',
]
const EXEMPLO_LIMITACAO = '- Limitação 999'

interface ElementoDaPrevia {
	chave: string
	campo: CampoDoModeloDeEtiqueta
	texto: string
}

@Component
export default class PreviaDeImpressaoDeEtiqueta extends Vue {
	@Prop({ type: Object, required: true }) tamanho!: TamanhoDaEtiqueta
	@Prop({ type: Array, required: true }) campos!: CampoDoModeloDeEtiqueta[]
	@Prop({ type: Array, required: true }) camposDisponiveis!: CampoDeEtiquetaDisponivel[]
	@Prop({ type: Number, default: 1 }) tabelasSimuladas!: number

	get elementos(): ElementoDaPrevia[] {
		return this.campos.map(campo => ({
			chave: campo.id,
			campo,
			texto: this.textoDoCampo(campo),
		}))
	}

	textoDoCampo(campo: CampoDoModeloDeEtiqueta): string {
		if (campo.campo !== 'PRECO_DA_TABELA') {
			return this.valorDeExemploDoCatalogo(campo.campo)
		}
		const linhas: string[] = []
		for (let tabela = 0; tabela < this.tabelasSimuladas; tabela += 1) {
			linhas.push(montarTextoDePrecoDaTabela(
				campo,
				EXEMPLOS_DE_TABELA[tabela % EXEMPLOS_DE_TABELA.length],
				this.valorDeExemploDoCatalogo('PRECO_DA_TABELA'),
				EXEMPLO_LIMITACAO,
			))
		}
		return linhas.join(QUEBRA_DE_LINHA)
	}

	get gridColunas(): number {
		if (this.tamanho.impressaoUnica) return 1
		const passo = (this.tamanho.larguraEtiqueta || 0) + (this.tamanho.distanciaEntreEtiquetas || 0)
		if (passo <= 0) return 0
		const disponivel = (this.tamanho.larguraPagina || 0) - (this.tamanho.bordaEsquerda || 0) + (this.tamanho.distanciaEntreEtiquetas || 0)
		return Math.max(0, Math.floor(disponivel / passo))
	}

	get gridLinhas(): number {
		if (this.tamanho.impressaoUnica) return 1
		const passo = (this.tamanho.alturaEtiqueta || 0) + (this.tamanho.distanciaEntreEtiquetas || 0)
		if (passo <= 0) return 0
		const disponivel = (this.tamanho.alturaPagina || 0) - 2 * (this.tamanho.bordaDoTopo || 0) + (this.tamanho.distanciaEntreEtiquetas || 0)
		return Math.max(0, Math.floor(disponivel / passo))
	}

	get escala(): number {
		if (!this.tamanho.larguraPagina || !this.tamanho.alturaPagina) return 1
		return Math.min(LARGURA_MAXIMA_PX / this.tamanho.larguraPagina, ALTURA_MAXIMA_PX / this.tamanho.alturaPagina)
	}

	get estiloDaPagina() {
		return {
			position: 'relative',
			width: `${(this.tamanho.larguraPagina || 0) * this.escala}px`,
			height: `${(this.tamanho.alturaPagina || 0) * this.escala}px`,
		}
	}

	get celulas() {
		const escala = this.escala
		const colunas = Math.min(this.gridColunas, 6)
		const linhas = Math.min(this.gridLinhas, 5)
		const celulas: { estilo: Record<string, string> }[] = []
		for (let linha = 0; linha < linhas; linha++) {
			for (let coluna = 0; coluna < colunas; coluna++) {
				const x = this.tamanho.impressaoUnica ? 0 : (this.tamanho.bordaEsquerda || 0) + coluna * ((this.tamanho.larguraEtiqueta || 0) + (this.tamanho.distanciaEntreEtiquetas || 0))
				const y = this.tamanho.impressaoUnica ? 0 : (this.tamanho.bordaDoTopo || 0) + linha * ((this.tamanho.alturaEtiqueta || 0) + (this.tamanho.distanciaEntreEtiquetas || 0))
				celulas.push({
					estilo: {
						position: 'absolute',
						left: `${x * escala}px`,
						top: `${y * escala}px`,
						width: `${(this.tamanho.larguraEtiqueta || 0) * escala}px`,
						height: `${(this.tamanho.alturaEtiqueta || 0) * escala}px`,
					},
				})
			}
		}
		return celulas
	}

	tipoDeDado(campo: CampoDoModeloDeEtiqueta): string {
		return this.camposDisponiveis.find(disponivel => disponivel.chave === campo.campo)?.tipoDeDado || 'TEXTO'
	}

	valorDeExemploDoCatalogo(chave: CampoDeEtiqueta | null): string {
		return this.camposDisponiveis.find(disponivel => disponivel.chave === chave)?.valorDeExemplo || ''
	}

	estiloDoElemento(elemento: ElementoDaPrevia) {
		const campo = elemento.campo
		const alinhamento: Record<TipoDeAlinhamentoDeTexto, string> = {
			ESQUERDA: 'left',
			CENTRO: 'center',
			DIREITA: 'right',
		}
		const justificar: Record<TipoDeAlinhamentoDeTexto, string> = {
			ESQUERDA: 'flex-start',
			CENTRO: 'center',
			DIREITA: 'flex-end',
		}
		const escala = this.escala
		return {
			position: 'absolute',
			display: 'flex',
			alignItems: 'center',
			justifyContent: justificar[campo.alinhamento] || 'flex-start',
			left: `${campo.x * escala}px`,
			top: `${campo.y * escala}px`,
			width: `${campo.largura * escala}px`,
			height: `${campo.altura * escala}px`,
			fontWeight: campo.negrito ? 'bold' : 'normal',
			textAlign: alinhamento[campo.alinhamento] || 'left',
			color: '#212121',
			overflow: 'hidden',
		}
	}

	get ajustesPorElemento(): Record<string, TextoAjustado> {
		const ajustes: Record<string, TextoAjustado> = {}
		for (const elemento of this.elementos) {
			if (this.tipoDeDado(elemento.campo) === 'GRAFICO') continue
			ajustes[elemento.chave] = ajustarTextoNaCaixa(
				elemento.texto, elemento.campo.tamanhoDaFonte, elemento.campo.largura, elemento.campo.altura, elemento.campo.negrito,
			)
		}
		return ajustes
	}

	linhasDoElemento(elemento: ElementoDaPrevia): string[] {
		return this.ajustesPorElemento[elemento.chave]?.linhas || []
	}

	estiloDoTextoDoElemento(elemento: ElementoDaPrevia) {
		const ajuste = this.ajustesPorElemento[elemento.chave]
		if (!ajuste) return {}
		return {
			fontSize: `${(ajuste.tamanhoDaFontePt / PONTOS_POR_CM) * this.escala}px`,
			lineHeight: `${(ajuste.alturaDaLinhaPt / PONTOS_POR_CM) * this.escala}px`,
		}
	}

	estiloDasBarras(campo: CampoDoModeloDeEtiqueta) {
		const moduloEmDots = moduloDoCodigoDeBarrasEmDots(campo.largura)
		const modulo = Math.max(1, moduloEmCm(moduloEmDots) * this.escala)
		return {
			width: `${larguraDoSimboloEmCm(moduloEmDots) * this.escala}px`,
			backgroundImage: 'repeating-linear-gradient(90deg,'
				+ `#111 0, #111 ${modulo}px,`
				+ `transparent ${modulo}px, transparent ${modulo * 2}px,`
				+ `#111 ${modulo * 2}px, #111 ${modulo * 4}px,`
				+ `transparent ${modulo * 4}px, transparent ${modulo * 5}px,`
				+ `#111 ${modulo * 5}px, #111 ${modulo * 6}px,`
				+ `transparent ${modulo * 6}px, transparent ${modulo * 8}px)`,
		}
	}
}
